import React, { useEffect, useState } from 'react';
import {
    TextField,
    Box,
    Button,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import axios from 'axios';
import TableLoader from '../GlobalComponents/TableLoader';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { convertHashRate } from '../../utils/HashrateUtils';
import { MachineModelInfo } from '../Machines/types';

interface EmitterHash {
    worker: string;
    hash15Min: number;
    hash24Hr: number;
    status: 'ONLINE' | 'OFFLINE';
    coin: string | null;
    workerList: string[];
    hashrate: number;
    pool: any;
}

interface TransformedData {
    emitter: string;
    worker: string;
    model: string;
    expectedHashRate: number;
    expectedHashUnit: string;
    actualHashRate: number;
    hashrate: number;
    workerList: string[];
    workerLength: number;
}

interface AccumulatedData {
    worker: string;
    emitters: string;
    hashrate: number;
    model: string;
    expectedHashRate: number;
    expectedHashUnit: string;
    actualHashRate: number;
}

export interface SelectedDataItem {
    id: number;
    name: string;
    hashrate: string;
    hashUnit: string;
}

function WorkerList() {
    const [rows, setRows] = useState<TransformedData[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState<string[]>([]);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<'hashrate' | 'emitters' | 'worker' | 'actualHashRate' | 'model' | 'expectedHashRate'>('hashrate');
    const [searchOptions, setSearchOptions] = useState<string[]>([]);

    useEffect(() => {
        fetchEmitterHashList();
    }, []);

    const fetchEmitterHashList = async () => {
        setLoading(true);
        try {
            const response1 = await axios.get(API_PATH + '/monitor/emitterhash', getAxiosConfig());
            const emitterHashData: EmitterHash[] = response1.data;

            const response2 = await axios.get(
                API_PATH + '/machines/list?key=&customer=&model=&state=null&page=0&limit=999999',
                getAxiosConfig()
            );
            const machineData: any[] = response2.data.content;

            const response3 = await axios.get(API_PATH + '/machine_model/list', getAxiosConfig());
            const machineModelData: MachineModelInfo[] = response3.data;

            const machineMap = new Map<string, string>(
                machineData.map((item) => [item.workerId, item.machineModel.modelName])
            );

            const modelMap = new Map<string, { hashRate: number; hashUnit: string }>(
                machineModelData.map((item) => [
                    item.modelName,
                    { hashRate: item.hashRate, hashUnit: item.hashUnit },
                ])
            );

            const transformedData = emitterHashData.flatMap((item) =>

                item.workerList
                    ? item.workerList.map((emitter) => {
                        const modelName = machineMap.get(emitter) || null;
                        const modelInfo = modelName ? modelMap.get(modelName) : null;
                        return {
                            worker: emitter,
                            emitter: item.worker,
                            workerLength: item.workerList.length,
                            model: modelName,
                            expectedHashRate: modelInfo?.hashRate || 0,
                            expectedHashUnit: modelInfo?.hashUnit || '',
                            actualHashRate: item.hash24Hr / item.workerList.length,
                        };
                    })
                    : []
            );
            
            const options = Array.from(
                new Set(
                    transformedData.map(item => item.worker.split('.')[0].toLowerCase())
                )
            ).map(worker =>
                transformedData.find(item => item.worker.split('.')[0].toLowerCase() === worker)?.worker.split('.')[0]
            ).filter((worker): worker is string => worker !== undefined);
            setSearchOptions(options);

            const emitterResponse = await axios.get(API_PATH + '/nicehash/emitters', getAxiosConfig());
            const emitterData = emitterResponse.data;
            const mergedData = transformedData.map((emitterHash:any) => {
                const matchingEmitter = emitterData.find((emitter: any) => emitter.pool.username === emitterHash.emitter);

                if (matchingEmitter) {
                    return {
                        ...emitterHash,
                        hashrate: convertHashRate(matchingEmitter.limit, matchingEmitter.pool.algorithm) / emitterHash.workerLength || 0
                    };
                } else {
                    return {
                        ...emitterHash,
                        hashrate: 0
                    };
                }
            });

            setRows(mergedData);
        } catch (error) {
            console.error('Error fetching emitter data:', error);
            setError('Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    const filteredRows = rows.filter(row => {
        if (searchTerm.length === 0) return true;
        return searchTerm.some(term => row.worker.trim().toLowerCase().includes(term.toLowerCase()));
    });

    const reducedData = Object.values(
        filteredRows.reduce<{ [key: string]: AccumulatedData }>(
            (acc, current) => {
                const key = current.worker;
                if (!acc[key]) {
                    acc[key] = {
                        worker: current.worker,
                        emitters: current.emitter,
                        model: current.model,
                        hashrate: current.hashrate,
                        expectedHashRate: current.expectedHashRate,
                        expectedHashUnit: current.expectedHashUnit,
                        actualHashRate: current.actualHashRate,
                    };
                } else {
                    const emitterRegex = new RegExp(`(${current.emitter})( \\((\\d+)\\))?`);
                    const match = acc[key].emitters.match(emitterRegex);

                    if (match) {
                        const currentCount = match[3] ? parseInt(match[3], 10) : 1;
                        const newCount = currentCount + 1;
                        acc[key].emitters = acc[key].emitters.replace(emitterRegex, `${current.emitter}${newCount > 1 ? ` (${newCount})` : ''}`);
                    } else {
                        acc[key].emitters += ` #${current.emitter}`;
                    }
                    acc[key].hashrate += current.hashrate;
                }
                return acc;
            },
            {}
        )
    );


    const handleRequestSort = (property: 'hashrate' | 'emitters' | 'worker' | 'actualHashRate' | 'model' | 'expectedHashRate') => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const getComparator = (order: 'asc' | 'desc', orderBy: 'hashrate' | 'emitters' | 'worker' | 'actualHashRate' | 'model' | 'expectedHashRate') => {
        return (a: AccumulatedData, b: AccumulatedData) => {
            if (orderBy === 'emitters') {
                const emittersA = a.emitters.toLowerCase();
                const emittersB = b.emitters.toLowerCase();
                if (emittersB < emittersA) return order === 'desc' ? -1 : 1;
                if (emittersB > emittersA) return order === 'desc' ? 1 : -1;
            } else if (orderBy === 'worker') {
                const workerA = a.worker.toLowerCase();
                const workerB = b.worker.toLowerCase();
                if (workerB < workerA) return order === 'desc' ? -1 : 1;
                if (workerB > workerA) return order === 'desc' ? 1 : -1;
            } else if (orderBy === 'model') {
                const extractKey = (model: string | null | undefined) => {
                    if (!model) {
                        return { prefix: '', number: Infinity };
                    }
                    const match = model.match(/^(.*?)(?:\s*\((\d+)\))?$/);
                    if (match) {
                        return {
                            prefix: match[1].trim(),
                            number: match[2] ? parseInt(match[2], 10) : Infinity
                        };
                    }
                    return { prefix: model.trim(), number: Infinity };
                };
                const modelA = extractKey(a.model);
                const modelB = extractKey(b.model);
                if (modelA.prefix !== modelB.prefix) {
                    return order === 'desc'
                        ? modelB.prefix.localeCompare(modelA.prefix)
                        : modelA.prefix.localeCompare(modelB.prefix);
                }
                return order === 'desc'
                    ? modelB.number - modelA.number
                    : modelA.number - modelB.number;
            }

            else if (orderBy === 'actualHashRate') {
                if (b.actualHashRate < a.actualHashRate) return order === 'desc' ? -1 : 1;
                if (b.actualHashRate > a.actualHashRate) return order === 'desc' ? 1 : -1;
            } else if (orderBy === 'expectedHashRate') {
                if (b.expectedHashRate < a.expectedHashRate) return order === 'desc' ? -1 : 1;
                if (b.expectedHashRate > a.expectedHashRate) return order === 'desc' ? 1 : -1;
            }
            return 0;
        };
    };

    const stableSort = (array: AccumulatedData[], comparator: (a: AccumulatedData, b: AccumulatedData) => number) => {
        const stabilizedThis = array.map((el, index) => [el, index] as [AccumulatedData, number]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    };

    const downloadCSV = (data: any[]) => {
        const headers = ['Worker', 'Emitters', 'Expected Hashrate', 'Actual Hashrate'];
        const rows = data.map(row => [
            row.worker,
            row.emitters,
            row.model,
            row.expectedHashRate,
            row.actualHashRate.toFixed(2)
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(',')),
        ].join('\n');

        const now = new Date();
        const padZero = (num: number) => num.toString().padStart(2, '0');
        const day = padZero(now.getDate());
        const month = padZero(now.getMonth() + 1);
        const fileName = `WorkerData_${day}-${month}.csv`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                <Button variant="contained" sx={{ mb: 2, mr: 2 }} size='small' onClick={() => downloadCSV(stableSort(reducedData, getComparator(order, orderBy)))}>
                    <Download />
                </Button>
                <Autocomplete
                    multiple
                    options={searchOptions}
                    value={searchTerm}
                    sx={{ width: '40%', mb: 2 }}
                    onChange={(event, newValue) => setSearchTerm(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"

                            label="Search"
                        />
                    )}
                />
            </Box>
            {loading ? (
                <TableLoader />
            ) : (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#333' : '#f5f5f5', color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000', padding: '6px' }}>
                                <TableCell align="left">
                                    <TableSortLabel active={orderBy === 'worker'} direction={order} onClick={() => handleRequestSort('worker')}>
                                        Worker ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel active={orderBy === 'emitters'} direction={order} onClick={() => handleRequestSort('emitters')}>
                                        Emitters
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel active={orderBy === 'model'} direction={order} onClick={() => handleRequestSort('model')}>
                                        Model
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel active={orderBy === 'expectedHashRate'} direction={order} onClick={() => handleRequestSort('expectedHashRate')}>
                                        Expected Hashrate(TH)
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="center">
                                    <TableSortLabel active={orderBy === 'actualHashRate'} direction={order} onClick={() => handleRequestSort('actualHashRate')}>
                                        Actual Hashrate(TH)
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stableSort(reducedData, getComparator(order, orderBy)).map((row) => (
                                <TableRow key={row.worker} >
                                    <TableCell align="left" >{row.worker}</TableCell>
                                    <TableCell align="center" >{row.emitters}</TableCell>
                                    <TableCell align="center" >{row.model}</TableCell>
                                    <TableCell align="center" >{row.expectedHashRate}</TableCell>
                                    <TableCell align="center" >{row.actualHashRate.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    );
}

export default WorkerList;
